import { FormattedMessage } from 'react-intl'
import { useNavigate, useParams } from 'react-router'
import { useDialog } from '@hooks/useDialog'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import { Suspense, useState } from 'react'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import ChartSkeleton from '@components/skeletons/ChartSkeleton/ChartSkeleton'
import {
  useConnectorData,
  useConnectorCRUD,
  useAssocInfo
} from '@hooks/useConnectors'

import errorDialog from '@static/images/dialog/error-dialog.png'
import successDialog from '@static/images/dialog/success-dialog.png'
import './ConnectorsDialog.css'

type ConnectorsDialogMode = 'assoc' | 'info'

const ConnectorsDialogSkeleton = ({ mode }: { mode: ConnectorsDialogMode }) => (
  <>
    {[...Array(4)].map((e, i) => (
      <ChartSkeleton key={i + e} />
    ))}
    <div className="dialog-actions">
      <button
        disabled
        type="button"
        className={mode === 'info' ? 'disconnect-button' : 'highlight-button'}
      >
        <FormattedMessage
          id={
            mode === 'info'
              ? 'profile.connectors-dialog.disconnect-button'
              : 'profile.connectors-dialog.form-button'
          }
        />
      </button>
    </div>
  </>
)

interface ContentProps {
  mode: ConnectorsDialogMode
  id: string
  connector: string
  title: string | null
  connectorId: string
}

const ConnectorsDialogContent = ({
  mode,
  id,
  connector,
  title,
  connectorId
}: ContentProps) => {
  const navigate = useNavigate()
  const { confirmConnector, deleteConnector, refreshConnectors } =
    useConnectorCRUD()

  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle')

  const closeTo = mode === 'info' ? '/profile/connectors' : '/profile'

  const handleAction = async () => {
    setStatus('loading')
    try {
      if (mode === 'info') {
        await deleteConnector(id)
      } else {
        await confirmConnector(id)
      }
      await refreshConnectors()
      setStatus('success')
    } catch (error) {
      console.error('Error on connector action:', error)
      setStatus('error')
    }
  }

  const handleClose = () => navigate(closeTo)
  const handleRetry = () => setStatus('idle')

  const labelBase =
    mode === 'info'
      ? 'profile.connectors-dialog.info-label'
      : 'profile.connectors-dialog.form-label'
  const labelSuffix =
    status === 'success' ? '.success' : status === 'error' ? '.error' : ''

  return (
    <>
      <h2>
        <FormattedMessage id={`${labelBase}${labelSuffix}`} />
      </h2>
      {status === 'success' && (
        <>
          <img src={successDialog} alt="Success" className="result-image" />
          <div className="dialog-actions">
            <button
              type="button"
              onClick={handleClose}
              className="standart-button"
            >
              <FormattedMessage
                id={
                  mode === 'info'
                    ? 'profile.connectors-dialog.back-button'
                    : 'profile.connectors-dialog.close-button'
                }
              />
            </button>
          </div>
        </>
      )}
      {status === 'error' && (
        <>
          <img src={errorDialog} alt="Error" className="result-image" />
          <div className="dialog-actions">
            <button
              type="button"
              onClick={handleRetry}
              className="standart-button"
            >
              <FormattedMessage id="profile.connectors-dialog.retry-button" />
            </button>
          </div>
        </>
      )}
      {(status === 'idle' || status === 'loading') && (
        <div className="connectors-info">
          <h3>
            <FormattedMessage id="profile.connectors-dialog.form-connector" />
          </h3>
          <p>{connector}</p>
          <h3>
            <FormattedMessage id="profile.connectors-dialog.form-title" />
          </h3>
          <p>{title}</p>
          <h3>
            <FormattedMessage id="profile.connectors-dialog.form-id" />
          </h3>
          <p>{connectorId}</p>
          <div className="dialog-actions">
            <button
              type="button"
              onClick={handleAction}
              className={
                mode === 'info' ? 'disconnect-button' : 'highlight-button'
              }
              disabled={status === 'loading'}
            >
              {status === 'loading' ? (
                <span className="icon icon-share" />
              ) : (
                <FormattedMessage
                  id={
                    mode === 'info'
                      ? 'profile.connectors-dialog.disconnect-button'
                      : 'profile.connectors-dialog.form-button'
                  }
                />
              )}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
const InfoConnectorWrapper = ({
  id,
  mode
}: {
  id: string
  mode: ConnectorsDialogMode
}) => {
  const data = useConnectorData(id)
  return (
    <ConnectorsDialogContent
      mode={mode}
      id={id}
      connector={data?.connector}
      title={data?.title}
      connectorId={data?.connectorId}
    />
  )
}

const AssocConnectorWrapper = ({
  id,
  mode
}: {
  id: string
  mode: ConnectorsDialogMode
}) => {
  const data = useAssocInfo(id)
  return (
    <ConnectorsDialogContent
      mode={mode}
      id={id}
      connector={data?.connector}
      title={data?.title}
      connectorId={data?.connectorId}
    />
  )
}

const ConnectorsDialogInner = ({ mode }: { mode: ConnectorsDialogMode }) => {
  const { id } = useParams()

  if (!id) return null
  if (!__CLIENT__) {
    return (
      <dialog id="profile-connectors-dialog" className="app-dialog">
        <div className="dialog-inner">
          <button
            type="button"
            className="standart-button icon icon-cross"
            disabled
          />
          <span className="icon icon-link" />
          <ConnectorsDialogSkeleton mode={mode} />
        </div>
      </dialog>
    )
  }

  return mode === 'info' ? (
    <InfoConnectorWrapper id={id} mode={mode} />
  ) : (
    <AssocConnectorWrapper id={id} mode={mode} />
  )
}

export default function ConnectorsDialog({
  mode
}: {
  mode: ConnectorsDialogMode
}) {
  const navigate = useNavigate()
  const closeTo = mode === 'info' ? '/profile/connectors' : '/profile'
  const handleClose = () => navigate(closeTo)

  const { dialogRef, handleBackdropClick } = useDialog(true, handleClose)

  return (
    <dialog
      id="profile-connectors-dialog"
      className="app-dialog"
      ref={dialogRef}
      onClick={handleBackdropClick}
      onCancel={handleClose}
    >
      <div className="dialog-inner">
        <button
          type="button"
          onClick={handleClose}
          className="standart-button icon icon-cross"
        />
        <span className="icon icon-link" />
        <ErrorBoundary errorComponent={ErrorMessage}>
          <Suspense fallback={<ConnectorsDialogSkeleton mode={mode} />}>
            <ConnectorsDialogInner mode={mode} />
          </Suspense>
        </ErrorBoundary>
      </div>
    </dialog>
  )
}
